/**
 * Minimal DOM helpers.
 *
 * Deliberately tiny: enough to build accessible markup declaratively without
 * ever reaching for `innerHTML` with user data. Every component in the app uses
 * these so the resulting DOM is consistent and XSS-safe by construction.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * Create an element.
 *
 * @param {string} tag - tag name, optionally with `.class` and `#id` suffixes
 *   (`'button.btn.primary#generate'`).
 * @param {object} [props] - properties, attributes and event handlers.
 *   - `class` / `className`: string or array (falsy entries dropped)
 *   - `dataset`: object of data attributes
 *   - `style`: object of CSS properties
 *   - `on`: object of event handlers (`{ click: fn }`)
 *   - `attrs`: object written with `setAttribute` (use for aria-*, role, etc.)
 *   - anything else is assigned as a property when it exists on the element,
 *     otherwise as an attribute.
 * @param {Array|Node|string} [children]
 */
export function el(tag, props = {}, children = []) {
  const { name, classes, id } = parseTag(tag);
  const node = document.createElement(name);

  if (id) node.id = id;
  if (classes.length) node.classList.add(...classes);

  applyProps(node, props);
  append(node, children);
  return node;
}

/** Same as {@link el} but for SVG elements. */
export function svgEl(tag, props = {}, children = []) {
  const { name, classes, id } = parseTag(tag);
  const node = document.createElementNS(SVG_NS, name);
  if (id) node.setAttribute('id', id);
  if (classes.length) node.setAttribute('class', classes.join(' '));
  for (const [key, value] of Object.entries(props)) {
    if (key === 'on') {
      for (const [event, handler] of Object.entries(value)) node.addEventListener(event, handler);
    } else if (value !== null && value !== undefined && value !== false) {
      node.setAttribute(key, String(value));
    }
  }
  append(node, children);
  return node;
}

function parseTag(tag) {
  const classes = [];
  let id = '';
  const name = String(tag)
    .replace(/#([\w-]+)/g, (_, value) => {
      id = value;
      return '';
    })
    .replace(/\.([\w-]+)/g, (_, value) => {
      classes.push(value);
      return '';
    }) || 'div';
  return { name, classes, id };
}

function applyProps(node, props) {
  for (const [key, value] of Object.entries(props ?? {})) {
    if (value === null || value === undefined) continue;

    if (key === 'class' || key === 'className') {
      const list = Array.isArray(value) ? value : String(value).split(/\s+/);
      node.classList.add(...list.filter(Boolean));
    } else if (key === 'dataset') {
      for (const [dataKey, dataValue] of Object.entries(value)) {
        if (dataValue !== null && dataValue !== undefined) node.dataset[dataKey] = String(dataValue);
      }
    } else if (key === 'style' && typeof value === 'object') {
      for (const [styleKey, styleValue] of Object.entries(value)) {
        if (styleValue !== null && styleValue !== undefined) {
          node.style.setProperty(styleKey.startsWith('--') ? styleKey : kebab(styleKey), String(styleValue));
        }
      }
    } else if (key === 'on' && typeof value === 'object') {
      for (const [event, handler] of Object.entries(value)) {
        if (typeof handler === 'function') node.addEventListener(event, handler);
      }
    } else if (key === 'attrs' && typeof value === 'object') {
      for (const [attr, attrValue] of Object.entries(value)) {
        if (attrValue === false || attrValue === null || attrValue === undefined) continue;
        node.setAttribute(attr, attrValue === true ? '' : String(attrValue));
      }
    } else if (key === 'text') {
      node.textContent = String(value);
    } else if (key === 'html') {
      // Only ever used with markup this codebase authored (e.g. inline icons).
      node.innerHTML = String(value);
    } else if (key in node) {
      node[key] = value;
    } else {
      node.setAttribute(key, value === true ? '' : String(value));
    }
  }
}

function kebab(value) {
  return value.replace(/[A-Z]/g, (char) => `-${char.toLowerCase()}`);
}

/** Append children of mixed types (nodes, strings, numbers, arrays, falsy). */
export function append(parent, children) {
  const list = Array.isArray(children) ? children : [children];
  for (const child of list) {
    if (child === null || child === undefined || child === false || child === true) continue;
    if (Array.isArray(child)) {
      append(parent, child);
    } else if (child instanceof Node) {
      parent.appendChild(child);
    } else {
      parent.appendChild(document.createTextNode(String(child)));
    }
  }
  return parent;
}

/** Build a document fragment. */
export function frag(children = []) {
  return append(document.createDocumentFragment(), children);
}

/** Remove every child of a node. */
export function clear(node) {
  while (node?.firstChild) node.removeChild(node.firstChild);
  return node;
}

/** Replace a node's children in one go. */
export function replaceChildren(node, children) {
  clear(node);
  append(node, children);
  return node;
}

/**
 * Add an event listener and return a disposer - components collect these and
 * call them all in `destroy()`.
 */
export function on(target, event, handler, options) {
  target.addEventListener(event, handler, options);
  return () => target.removeEventListener(event, handler, options);
}

/** Delegated listener: fires when the event originates inside `selector`. */
export function delegate(root, event, selector, handler, options) {
  const listener = (nativeEvent) => {
    const match = nativeEvent.target instanceof Element
      ? nativeEvent.target.closest(selector)
      : null;
    if (match && root.contains(match)) handler(nativeEvent, match);
  };
  return on(root, event, listener, options);
}

/** Convenience: a labelled form field wrapper. */
export function field({ id, label, help, control, inline = false }) {
  return el('div', { class: ['field', inline && 'field--inline'] }, [
    el('label', { class: 'field__label', htmlFor: id, text: label }),
    control,
    help ? el('p', { class: 'field__help', id: `${id}-help`, text: help }) : null,
  ]);
}

/** Visually hidden text for screen readers. */
export function srOnly(text) {
  return el('span', { class: 'sr-only', text });
}

/** All focusable descendants, in tab order. */
export function focusableWithin(root) {
  const selector = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
  ].join(',');
  return [...root.querySelectorAll(selector)].filter(
    (node) => node.offsetParent !== null || node === document.activeElement,
  );
}

/** Trap Tab focus inside a container. Returns a disposer. */
export function trapFocus(container) {
  return on(container, 'keydown', (event) => {
    if (event.key !== 'Tab') return;
    const focusable = focusableWithin(container);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
}

/** Debounce a function (trailing edge). */
export function debounce(fn, waitMs = 200) {
  let timer = null;
  const debounced = (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      fn(...args);
    }, waitMs);
  };
  debounced.cancel = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };
  debounced.flush = (...args) => {
    if (timer) clearTimeout(timer);
    timer = null;
    fn(...args);
  };
  return debounced;
}

/** Shallow equality - used by components to skip redundant re-renders. */
export function shallowEqual(a, b) {
  if (a === b) return true;
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => a[key] === b[key]);
}
